import type { ModelId, ViewId, ViewModelId } from 'balus-common';
import type { Position } from '@view-model/models/common/types/ui';
import { useCommandManager } from '@model-framework/command';
import { useMemo, useRef } from 'react';
import { ViewOperation } from '@view-model/adapter';
import type { ViewContextType } from '@view-model/ui/components/View/ViewContext';
import { ViewContext } from '@view-model/ui/components/View/ViewContext';

type Props = {
  viewModelId: ViewModelId;
  viewId: ViewId;
  modelId: ModelId;
  children: React.ReactNode;
  viewPosition: Position;
};

export const ViewContextProvider: React.FC<Props> = ({
  viewModelId,
  viewId,
  modelId,
  children,
  viewPosition,
}: Props) => {
  const commandManager = useCommandManager();

  // https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  const viewOperationRef = useRef<ViewOperation | null>(null);
  if (viewOperationRef.current === null) {
    viewOperationRef.current = new ViewOperation(viewModelId, viewId, modelId, commandManager);
  }
  const viewOperation = viewOperationRef.current;

  // どれか 1 つのビューの位置が更新されるたびに、全ビューの viewPosition が同じ座標の新しいオブジェクトで渡ってくるため、
  // 数値で比較して value の参照を保ち、memo 化された子孫の再レンダーを防ぐ
  const { x, y } = viewPosition;
  const value = useMemo<ViewContextType>(() => ({ viewOperation, viewPosition: { x, y } }), [viewOperation, x, y]);

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
};
